import AdminTeceoIcon from '@components/v2/icons/AdminTeceo';
import Avatar from '@components/v2/ui/Avatar/Avatar';
import Icon from '@components/v2/ui/Icon/Icon';
import ListItemAvatar from '@components/v2/ui/List/ListItemAvatar';
import ListItemIcon from '@components/v2/ui/List/ListItemIcon';
import ListItemText from '@components/v2/ui/List/ListItemText';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import { HoverSubmenuTriggerProps } from '@components/v2/ui/Menu/hooks/useHoverSubmenu.types';
import { Box, Divider } from '@mui/material';
import { ArrowLeftRight, ChevronRight, CircleUserRound, LogOut } from 'lucide-react';
import { type ReactNode } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import OfflineMenuItem from './components/OfflineMenuItem/OfflineMenuItem';
import { USER_MENU_ANCHOR_OFFSET, USER_MENU_EXTERNAL_LINKS } from './userMenu.consts';
import { UserMenuOfflineItemProps } from './userMenu.types';

interface UserMenuSlots {
  highlightsSection?: ReactNode;
}

interface UserMenuVisibility {
  account?: boolean;
  externalLinks?: boolean;
  signOut?: boolean;
}

interface UserMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  userName: string;
  userInitial: string;
  userLogoUrl?: string;
  isAdminTeceo: boolean;
  teamItemProps: HoverSubmenuTriggerProps & {
    show: boolean;
    isOpen: boolean;
  };
  userItemProps: {
    show: boolean;
    onClick: () => void;
  };
  offlineItemProps?: UserMenuOfflineItemProps;
  onAccountClick: () => void;
  onAdminClick: () => void;
  onSignOutClick: () => void;
  onExternalLinkClick: (url: string) => void;
  visibility?: UserMenuVisibility;
  slots?: UserMenuSlots;
}

const UserMenu = ({
  anchorEl,
  onClose,
  userInitial,
  userLogoUrl,
  isAdminTeceo,
  teamItemProps,
  userItemProps,
  offlineItemProps,
  userName,
  onAccountClick,
  onAdminClick,
  onSignOutClick,
  onExternalLinkClick,
  visibility,
  slots,
}: UserMenuProps) => {
  const { translate } = useTranslation();

  const {
    account: showAccount = true,
    externalLinks: showExternalLinks = true,
    signOut: showSignOut = true,
  } = visibility ?? {};

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ horizontal: 'left', vertical: USER_MENU_ANCHOR_OFFSET }}
      transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      marginThreshold={0}
      slotProps={{ paper: { sx: { minWidth: 150, maxWidth: 200 } } }}
    >
      {showAccount && (
        <MenuItem onClick={onAccountClick}>
          <ListItemAvatar>
            <Avatar src={userLogoUrl} alt={userName} fallback="initials" initials={userInitial} />
          </ListItemAvatar>
          <ListItemText primary={userName} slotProps={{ primary: { noWrap: true } }} />
        </MenuItem>
      )}

      {teamItemProps.show && (
        <MenuItem
          selected={teamItemProps.isOpen}
          onClick={teamItemProps.onClick}
          onMouseEnter={teamItemProps.onMouseEnter}
          onMouseLeave={teamItemProps.onMouseLeave}
        >
          <ListItemIcon>
            <Icon icon={ArrowLeftRight} size="md" />
          </ListItemIcon>
          <ListItemText primary={translate(LangEnum.MAIN_DRAWER_CHANGE_TEAM)} />
          <Box>
            <Icon icon={ChevronRight} size="xs" />
          </Box>
        </MenuItem>
      )}

      {offlineItemProps?.show && <OfflineMenuItem {...offlineItemProps} />}

      {userItemProps.show && (
        <MenuItem onClick={userItemProps.onClick}>
          <ListItemIcon>
            <Icon icon={CircleUserRound} size="md" />
          </ListItemIcon>
          <ListItemText primary={translate(LangEnum.MAIN_DRAWER_CHANGE_USER)} />
        </MenuItem>
      )}

      {isAdminTeceo && (
        <MenuItem onClick={onAdminClick}>
          <ListItemIcon>
            <Icon icon={AdminTeceoIcon} size="md" />
          </ListItemIcon>
          <ListItemText primary={translate(LangEnum.MAIN_DRAWER_TECEO_ADMIN)} />
        </MenuItem>
      )}

      {showExternalLinks && (
        <>
          <Divider sx={{ my: '4px !important' }} />

          {USER_MENU_EXTERNAL_LINKS.map(({ testId, label, icon, url }) => (
            <MenuItem key={testId} testId={testId} onClick={() => onExternalLinkClick(url)}>
              <ListItemIcon>
                <Icon icon={icon} size="md" />
              </ListItemIcon>
              <ListItemText primary={translate(label)} />
            </MenuItem>
          ))}
        </>
      )}

      {slots?.highlightsSection}

      {showSignOut && (
        <>
          <Divider sx={{ my: '4px !important' }} />

          <MenuItem onClick={onSignOutClick}>
            <ListItemIcon>
              <Icon icon={LogOut} size="md" />
            </ListItemIcon>
            <ListItemText primary={translate(LangEnum.MAIN_DRAWER_LOGOUT)} />
          </MenuItem>
        </>
      )}
    </Menu>
  );
};

export default UserMenu;
